/**
 * CompanyTypeahead — debounced typeahead over known companies parsed from
 * one import's positions (`GET /api/web/v2/bct/companies?import_id=&q=`), used
 * by the BCT run-creation form's Company field. Requires an import to be
 * selected first: when `importId` is null/empty the input is disabled with a
 * "Select a contact list first" hint and no query fires.
 *
 * Adapted from `new-import/steps/SearchTypeahead.tsx`: 300ms debounce (for what
 * the user types), TanStack Query with `placeholderData: prev => prev`, a 700ms
 * anti-flicker delay before the spinner, and match highlighting.
 *
 * Browse-on-focus: unlike SearchTypeahead, this typeahead HAS an initial view.
 * On first focus into an empty field (import already chosen) it fires
 * immediately with `q=""` — the backend returns the import's top companies (by
 * frequency, capped at 10). As the user types, filtering matches on EITHER the
 * company name OR the LinkedIn company ID, from the very first character (no
 * 2-char minimum). Match highlighting kicks in from 1 character too.
 *
 * Difference from SearchTypeahead: free-text is a first-class fallback. The
 * user can type a company name we've never seen, ignore the (empty)
 * suggestion list, and submit — the parent keeps `id: null` in that case.
 * Selecting a suggestion is what fills the Company ID (still editable
 * afterward, in the parent). Mirrors the v38 mock's `#page-bct`
 * `.bct-scope-row` behaviour.
 *
 * Tokenized: token color utilities + `text-*` roles only, no inline
 * `style={{}}`, no arbitrary `text-[Npx]`.
 */

import { Spinner } from "@nu-assist/ui";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import type { CompanySuggestion } from "../../clients/bct.client";

const SEARCH_DEBOUNCE_MS = 300;
const MIN_HIGHLIGHT_LENGTH = 1;
const SPINNER_DELAY_MS = 700;

export interface CompanyTypeaheadProps {
  /** Current company name (free text or a previously-picked suggestion). */
  value: string;
  /** True once a suggestion was picked — collapses to the selected summary. */
  selected: boolean;
  /** Selected import to scope suggestions to. When `null`/empty the field is
   * disabled (no import chosen yet) and no query ever fires. */
  importId: string | null;
  /** Free-text edit of the name (no suggestion picked). */
  onTextChange: (name: string) => void;
  /** A suggestion was picked — fills both name and id in the parent. */
  onSelect: (suggestion: CompanySuggestion) => void;
  /** Clears the picked state, back to free-text editing. */
  onClear: () => void;
  /** Returns known-company suggestions for `query` (`""` → the import's top
   * companies, browse mode). The parent binds the selected `import_id` and the
   * result limit into this call. */
  search: (query: string) => Promise<CompanySuggestion[]>;
  /** DOM id for the input (label association in the parent row). */
  inputId: string;
  /** Test id suffix — the parent row index. */
  index: number;
  /** Shared input className from the parent (design-system tokens). */
  inputClassName: string;
}

/**
 * Split `text` around case-insensitive occurrences of `query` and wrap each
 * match in a highlighted `<mark>`. Returns `text` unchanged for an empty
 * query (browse mode) or when `query` doesn't occur. Highlights from a single
 * character, since filtering now works from 1 char.
 */
function highlightMatch(text: string, query: string): React.ReactNode {
  const trimmed = query.trim();
  if (trimmed.length < MIN_HIGHLIGHT_LENGTH) return text;
  const escaped = trimmed.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const parts = text.split(new RegExp(`(${escaped})`, "gi"));
  if (parts.length === 1) return text;
  return parts.map((part, i) =>
    part.toLowerCase() === trimmed.toLowerCase() ? (
      <mark key={`${i}-${part}`} className="rounded-sm bg-mark text-mark-ink">
        {part}
      </mark>
    ) : (
      <span key={`${i}-${part}`}>{part}</span>
    )
  );
}

export function CompanyTypeahead({
  value,
  selected,
  importId,
  onTextChange,
  onSelect,
  onClear,
  search,
  inputId,
  index,
  inputClassName,
}: CompanyTypeaheadProps) {
  const [q, setQ] = useState("");
  const [focused, setFocused] = useState(false);
  const hasImport = !!importId && importId.trim() !== "";

  // Debounce the free-text `value` into `q`. There's no minimum-length gate
  // anymore: an empty `value` resolves to `q=""` (browse mode — the backend
  // returns the import's top companies), and anything typed filters from the
  // first character. While a suggestion is `selected`, the dropdown is
  // collapsed, so `q` stays "" and the query is disabled (see below).
  useEffect(() => {
    if (selected || !hasImport) {
      setQ("");
      return;
    }
    const id = setTimeout(() => setQ(value.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [value, selected, hasImport]);

  // The dropdown is open once the user has focused an enabled, non-collapsed
  // field. Browse-on-focus: the query is enabled immediately on focus with the
  // current `q` (""` on first focus into an empty field), without waiting for
  // the debounce — the debounce only governs subsequent keystrokes.
  const isOpen = hasImport && !selected && focused;

  const {
    data: items = [],
    isFetching,
    isError,
  } = useQuery({
    // `importId` and `q` are part of the key so switching imports (or typing)
    // invalidates the old cache instead of showing stale results.
    queryKey: ["bct-companies", importId, q],
    queryFn: () => (hasImport ? search(q) : Promise.resolve([])),
    enabled: isOpen,
    placeholderData: (prev) => prev,
    retry: false,
  });

  const [showSpinner, setShowSpinner] = useState(false);

  useEffect(() => {
    if (!isFetching) {
      setShowSpinner(false);
      return;
    }
    const id = setTimeout(() => setShowSpinner(true), SPINNER_DELAY_MS);
    return () => clearTimeout(id);
  }, [isFetching]);

  if (selected) {
    return (
      <div
        data-testid={`bct-company-selected-${index}`}
        className="flex items-center justify-between gap-3 rounded-sm border border-border bg-surface px-3 py-2"
      >
        <span className="truncate text-body text-text">{value}</span>
        <button
          type="button"
          data-testid={`bct-company-change-${index}`}
          onClick={onClear}
          className="shrink-0 text-caption text-accent hover:underline"
        >
          Change
        </button>
      </div>
    );
  }

  return (
    <div className="relative flex flex-col gap-1">
      <input
        id={inputId}
        type="text"
        className={inputClassName}
        placeholder={hasImport ? "Type a company name…" : "Select a people list first"}
        value={value}
        disabled={!hasImport}
        data-testid={`bct-company-name-${index}`}
        onChange={(e) => onTextChange(e.target.value)}
        onFocus={() => setFocused(true)}
        // Delay the close so a click on an option (which blurs the input first)
        // still lands before the dropdown unmounts.
        onBlur={() => setTimeout(() => setFocused(false), 150)}
        aria-expanded={isOpen}
        aria-autocomplete="list"
      />
      {isOpen ? (
        <div
          data-testid={`bct-company-options-${index}`}
          className="absolute left-0 right-0 top-full z-20 mt-1 flex max-h-56 flex-col gap-1 overflow-y-auto rounded-sm border border-border bg-bg p-1 shadow-lg"
        >
          {isError ? (
            <p
              data-testid={`bct-company-error-${index}`}
              className="px-1 py-2 text-caption text-warning"
            >
              Couldn’t load companies — you can still type a name
            </p>
          ) : (
            <>
              {showSpinner ? (
                <div
                  data-testid={`bct-company-loading-${index}`}
                  className="flex items-center gap-2 px-1 py-2 text-caption text-text-secondary"
                >
                  <span className="text-accent">
                    <Spinner size="sm" label="Searching companies" />
                  </span>
                  <span aria-hidden="true">Searching…</span>
                </div>
              ) : null}
              {items.length === 0 && !isFetching ? (
                <p className="px-1 py-2 text-caption text-text-muted">
                  {value.trim()
                    ? `No known companies match — press submit to use “${value.trim()}” as a new entry.`
                    : "No known companies for this people list yet — type a name to enter it manually."}
                </p>
              ) : (
                items.map((opt) => (
                  <button
                    key={`${opt.id}-${opt.name}`}
                    type="button"
                    data-testid={`bct-company-option-${index}-${opt.id}`}
                    // preventDefault keeps the input focused through the click so
                    // the option's onClick always lands — without it we'd race the
                    // onBlur close below (kept as a belt-and-suspenders fallback).
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => onSelect(opt)}
                    className="flex items-baseline justify-between gap-2 rounded-sm px-2 py-1.5 text-left hover:bg-accent-muted"
                  >
                    <span className="truncate text-body text-text">
                      {highlightMatch(opt.name, q)}
                    </span>
                    <span className="shrink-0 font-mono text-caption text-text-secondary">
                      (ID {opt.id})
                    </span>
                  </button>
                ))
              )}
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}
